/**
 * The Deploys section's data: a repository's deploy sources, what the
 * detector proposes from its workflows, and the check that shows what a
 * source matches. One repository at a time; a response for a repository no
 * longer selected is dropped.
 *
 * Nothing here saves on its own: a suggestion becomes a source only when a
 * person accepts it.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import type {
  DeploySource,
  DeploySourceCheck,
  DeploySourceDetection,
  StoredDeploySource,
} from "@yagni/shared";
import { captureUiError } from "../sentry.js";

const REQUEST_FAILED = "Deploy sources could not be loaded just now.";

async function call<T>(url: string, method: "GET" | "PUT" | "POST", body?: unknown): Promise<T> {
  const response = await fetch(url, {
    method,
    credentials: "include",
    ...(body === undefined ? {} : { headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) }),
  });
  let data: unknown = null;
  try {
    data = await response.json();
  } catch {
    data = null;
  }
  if (!response.ok) {
    const message =
      data && typeof data === "object" && typeof (data as { error?: unknown }).error === "string"
        ? (data as { error: string }).error
        : REQUEST_FAILED;
    throw new Error(message);
  }
  return data as T;
}

/** A stored source as the page sends it back: without its id and timestamp. */
export function asInput(source: StoredDeploySource | DeploySource): DeploySource {
  return { environment: source.environment, label: source.label, source: source.source };
}

export function useRepositoryDeploys(repoId: string | undefined) {
  const [sources, setSources] = useState<StoredDeploySource[]>([]);
  const [detection, setDetection] = useState<DeploySourceDetection | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const current = useRef(repoId);
  current.current = repoId;

  const base = repoId ? `/api/repos/${repoId}/deploy-sources` : null;

  const report = useCallback((err: unknown) => {
    setError(err instanceof Error ? err.message : REQUEST_FAILED);
    captureUiError(err, { area: "Settings", operation: "repository_deploy_sources" });
  }, []);

  const detect = useCallback(async () => {
    if (!base) return;
    const asked = repoId;
    try {
      const result = await call<Partial<DeploySourceDetection>>(`${base}/detect`, "POST", {});
      if (current.current === asked) {
        setDetection({
          workflows: Array.isArray(result?.workflows) ? result.workflows : [],
          suggestions: Array.isArray(result?.suggestions) ? result.suggestions : [],
        });
      }
    } catch (err) {
      if (current.current === asked) report(err);
    }
  }, [base, repoId, report]);

  useEffect(() => {
    setSources([]);
    setDetection(null);
    setLoaded(false);
    setError(null);
    if (!base) return;
    let live = true;
    void (async () => {
      try {
        const result = await call<{ sources?: StoredDeploySource[] }>(base, "GET");
        if (!live) return;
        const read = Array.isArray(result?.sources) ? result.sources : [];
        setSources(read);
        setLoaded(true);
        // A repository with no source yet: propose one from its workflows.
        if (read.length === 0) await detect();
      } catch (err) {
        if (live) {
          setLoaded(true);
          report(err);
        }
      }
    })();
    return () => {
      live = false;
    };
  }, [base, detect, report]);

  const save = useCallback(
    async (next: DeploySource[]): Promise<boolean> => {
      if (!base) return false;
      const asked = repoId;
      setBusy(true);
      setError(null);
      try {
        const result = await call<{ sources?: StoredDeploySource[] }>(base, "PUT", { sources: next });
        if (current.current === asked) setSources(Array.isArray(result?.sources) ? result.sources : []);
        return true;
      } catch (err) {
        if (current.current === asked) report(err);
        return false;
      } finally {
        setBusy(false);
      }
    },
    [base, repoId, report],
  );

  const check = useCallback(
    async (input: { workflowPath: string; jobName?: string }): Promise<DeploySourceCheck | null> => {
      if (!base) return null;
      try {
        return await call<DeploySourceCheck>(`${base}/check`, "POST", input);
      } catch (err) {
        report(err);
        return null;
      }
    },
    [base, report],
  );

  return { sources, detection, loaded, busy, error, save, detect, check };
}
